import { CSSProperties } from 'react';
import { Features } from '~src/interfaces/company.interface';

import { UserPermissions } from '~src/interfaces/session.types';
import { SvgIconName } from '~src/shared/SvgIcon';
import { routes } from '~src/routingConfig';
import { setup } from '../../../../config/server/server.config';
import { store } from '~src/index';
import { isAdmin } from '~src/store/session/session.reducer';
import { filterMenuCallback } from '~src/shared/sidebar/filterMenuCallback';

export interface SideBarSection {
  link: string;
  url?: string;
  requiredFeatures?: Partial<Features>;
  requiredPermissions?: UserPermissions[];
  icon?: SvgIconName;
}

export interface SubNavSection {
  id: string;
  items: SideBarSection[];
}

export interface SideBarItemI {
  name: string;
  paths?: string[];
  icon: SvgIconName;
  title?: string;
  requiredFeatures?: Partial<Features>;
  requiredPermissions?: UserPermissions[];
  sectionLinks?: SubNavSection[] | (() => SubNavSection[]);
  url?: string;
  style?: CSSProperties;
  viewBoxWidth?: number;
  hideMenuIfEmpty?: boolean;
  priority?: number; // less priority gets hidden first if real estate becomes scarce. defaulting to 0
}

const getSidebarLinks = function (): SideBarItemI[] {
  return [
    {
      name: 'company',
      icon: 'iconCircleStar',
      url: routes.sessionUser.home,
      hideMenuIfEmpty: true,
      paths: ['homehub'],
      sectionLinks: [{
        id: 'homehub',
        items: [{
          link: 'x.sidebar.subnav.link_label.employee_home',
          url: routes.sessionUser.home,
        }]
      }],
      priority: 3,
    },
    {
      name: 'search',
      icon: 'iconSearch',
      title: 'x.sidebar.subnav.header.answer_questions',
      requiredFeatures: { survey: {} },
    },
    {
      name: 'user',
      paths: ['user'],
      icon: 'iconPerson',
      sectionLinks: [
        {
          id: 'user',
          items: [
            {
              link: 'x.sidebar.subnav.link_label.employee_profile',
              url: routes.reports.sessionUser,
              requiredFeatures: { survey: {} },
            },
            {
              link: 'x.sidebar.subnav.link_label.employee_home',
              url: routes.sessionUser.home,
            }
          ],
        },
      ],
      priority: 2,
    },
    {
      name: 'content',
      paths: ['content'],
      icon: 'iconClipBoard',
      title: 'x.sidebar.subnav.header.manage_content',
      requiredFeatures: { survey: {} },
      requiredPermissions: [UserPermissions.ManageCompany],
      sectionLinks: [
        {
          id: 'content',
          items: [
            {
              link: 'x.sidebar.subnav.link_label.manage_surveys',
              url: routes.contentSurveys.surveys,
              requiredPermissions: [UserPermissions.ManageCompany],
              icon: 'iconManageSurveys'
            },
            {
              link: 'x.sidebar.subnav.link_label.manage_topics',
              url: routes.contentTopics.topics,
              requiredPermissions: [UserPermissions.ManageCompany],
              icon: 'iconEditTopicsQuestions'
            },
            {
              link: 'x.sidebar.subnav.link_label.manage_scales',
              url: routes.contentScales.scales,
              requiredPermissions: [UserPermissions.ManageCompany],
              icon: 'iconManageScales'
            },
            {
              link: 'x.sidebar.settings.performance_management',
              url: routes.contentScales.performanceManagement,
              requiredPermissions: [UserPermissions.ManageCompany],
              icon: 'iconGear'
            },
          ],
        },
      ],
    },
    {
      name: 'analysis',
      title: 'x.sidebar.compare.title',
      url: routes.analysis.heatmap,
      icon: 'iconReports',
      requiredFeatures: { survey: {} },
      requiredPermissions: [UserPermissions.ManageMe],
      paths: ['analysis'],
      sectionLinks: [
        {
          id: 'analysis',
          items: [
            {
              requiredFeatures: { survey: {} },
              link: 'x.sidebar.subnav.link_label.company_profile',
              url: routes.reports.company,
            },
            {
              link: 'x.sidebar.subnav.link_label.heatmap',
              url: routes.analysis.heatmap,
              requiredPermissions: [UserPermissions.ManageMe],
            },
            {
              link: 'x.sidebar.subnav.link_label.spider-web',
              url: routes.analysis.spider,
              requiredPermissions: [UserPermissions.ManageMe],
            },
            {
              link: 'x.sidebar.subnav.link_label.ai',
              url: routes.analysis.ai,
              requiredFeatures: { survey: { recommendations: true } },
              requiredPermissions: [UserPermissions.ManageMe],
            },
          ],
        },
      ],
      priority: 1,
    },
    {
      name: 'objectives',
      paths: ['okrs'],
      icon: 'iconTarget',
      title: 'x.sidebar.subnav.header.objectives_and_keyresults',
      requiredFeatures: { okr: {} },
      sectionLinks: [
        {
          id: 'overview',
          items: [{ link: 'x.sidebar.subnav.link_label.objectives_overview', url: routes.okrs.list }],
        },
        {
          id: 'key_result_stream',
          items: [{ link: 'key_result_stream' }],
        },
      ],
      priority: -1,
    },
    {
      name: 'spaceloop',
      url: setup.SPACELOOP_FE,
      icon: 'iconCircleClock',
      title: 'Spaceloop',
      requiredFeatures: { booking: {} },
      sectionLinks: [],
      priority: -2,
    },
    {
      name: 'settings',
      title: 'x.sidebar.subnav.header.settings',
      style: { marginTop: 'auto' },
      paths: ['settings'],
      icon: 'iconGear',
      sectionLinks: [
        {
          id: 'settings',
          items: [
            {
              link: 'x.sidebar.subnav.link_label.company_settings',
              url: routes.company.settings,
              requiredPermissions: [UserPermissions.ManageCompany],
            },
            { link: 'x.sidebar.subnav.link_label.personal_settings', url: routes.sessionUser.settings },
            {
              link: 'x.sidebar.subnav.link_label.user_management',
              url: routes.company.users,
              requiredPermissions: [UserPermissions.ManageCompany],
            },
            {
              link: 'x.sidebar.subnav.link_label.manage_attributes',
              url: routes.company.attributes,
              requiredPermissions: [UserPermissions.ManageCompany],
            },
          ],
        },
      ],
    },
    {
      name: 'help',
      paths: ['contact'],
      icon: 'iconCircleQuestion',
      sectionLinks: () => {
        const st = store.getState();
        const user = st.user.session.user;
        const nav = [
          {
            id: 'overview',
            items: [{ link: 'x.sidebar.contact.tooltip.title', url: `${routes.contact}?from=app` }],
          },
        ];
        if (!user) {
          return nav;
        }
        if (isAdmin(st)) {
          nav.push({
            id: 'knowledge-base-admin',
            items: [{ link: 'x.sidebar.knowledge_base_admin.tooltip.title', url: setup.ADMIN_KNOWLEDGEBASE_URL }],
          });
        }
        if (user.isManager) {
          nav.push({
            id: 'knowledge-base-manager',
            items: [
              { link: 'x.sidebar.knowledge_base_manager.tooltip.title', url: setup.MANAGER_KNOWLEDGE_BASE_URL },
            ],
          });
        }

        return nav;
      },
      priority: -3,
    },
  ];
};

const SIDEBAR_ITEM_HEIGHT_PX = 60;

export function dropToCount(sidebarItems: SideBarItemI[], maxCount: number ): void {
  while(sidebarItems.length > maxCount) {
    let spliceIdx = sidebarItems.length - 1; // by default, drop last item
    sidebarItems.forEach((sidebarItem: SideBarItemI, idx) => {
      if ((sidebarItem.priority || 0) < (sidebarItems[spliceIdx].priority || 0)) {
        spliceIdx = idx;
      }
    });
    sidebarItems.splice(spliceIdx, 1);
  }
}

export const getLeftSideNavigationItems = (
  permissions: UserPermissions[],
  enabledFeatures: Partial<Features>,
  windowHeight: number,
): SideBarItemI[] => {
  if (permissions.length < 1) {
    return [];
  }

  const sidebarLinks = getSidebarLinks();
  dropToCount(sidebarLinks, Math.floor(windowHeight / SIDEBAR_ITEM_HEIGHT_PX))
  sidebarLinks.forEach((item, index) => {
    const mainItem = item;
    if (mainItem.sectionLinks) {
      const links =
        typeof mainItem.sectionLinks === 'function' ? mainItem.sectionLinks() : mainItem.sectionLinks;
      sidebarLinks[index].sectionLinks = links.map((section) => ({
        ...section,
        items: section.items.filter((sectionItem) =>
          filterMenuCallback(
            sectionItem.requiredPermissions || [],
            permissions,
            sectionItem.requiredFeatures || {},
            enabledFeatures,
          ),
        ),
      }));
    }
  });

  return sidebarLinks.filter((item) =>
    filterMenuCallback(
      item.requiredPermissions || [],
      permissions,
      item.requiredFeatures || {},
      enabledFeatures,
    ),
  );
};
